import { type ReactNode, createContext, useContext, useMemo, useState } from 'react'

/**
 * App-wide RFQ TOTAL-record count, used by the Sidebar "RFQ" nav-item badge
 * (UID-529, ADR-0167). This is the account's total RFQ count in ANY status
 * (draft/requested/quoting/quoted/accepted/declined/expired) — a plain
 * `GET /rfq` list length, not a filtered subset and not the RFQ-bundle
 * accumulator (`rfq-bundle-context.tsx`, ADR-0048/UID-112), which is a
 * DIFFERENT, unsubmitted-parts-queued-for-composing concept that still
 * powers the "Add parts" flows and `RfqReviewPage` unchanged.
 *
 * ADR-0167 explicitly SUPERSEDES ADR-0048's nav-badge semantics (a cart-style
 * in-progress counter) with an inbox-style total-record count — this store
 * is that new, separate seam.
 *
 * Modeled 1:1 on `cart-count-context.tsx` (see its doc comment for the
 * pattern this mirrors): a tiny context + provider + hook, hydrated from
 * `useHydrateRfqCount` (`AppShell`), with an INERT no-provider fallback so
 * `Sidebar.test.tsx`'s many provider-less renders keep working.
 */
interface RfqCountValue {
  /** `undefined` = not yet known this session; a number once `GET /rfq` has loaded. */
  readonly totalCount: number | undefined
  setTotalCount(count: number | undefined): void
}

const RfqCountContext = createContext<RfqCountValue | undefined>(undefined)

export function RfqCountProvider({ children }: { children: ReactNode }) {
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined)
  const value = useMemo<RfqCountValue>(() => ({ totalCount, setTotalCount }), [totalCount])
  return <RfqCountContext.Provider value={value}>{children}</RfqCountContext.Provider>
}

/**
 * Tolerates being called with no provider mounted (returns an inert value),
 * so a component that reads the count — e.g. `Sidebar` — can be rendered in
 * isolation in tests without every such test having to wrap this provider.
 */
export function useRfqCount(): RfqCountValue {
  return useContext(RfqCountContext) ?? INERT
}

const INERT: RfqCountValue = { totalCount: undefined, setTotalCount: () => {} }
